/**
 * Sprout Total Pricing
 * Handles total price calculations and table row highlighting for sprout products only
 */

class SproutTotalPricing {
  constructor() {
    this.quantityBreaks = {
      default: null,  // Will be set from product price
      tiers: [
        { quantity: 3, discount: 0.05 }, // 5% discount
        { quantity: 6, discount: 0.10 }, // 10% discount  
        { quantity: 9, discount: 0.15 }  // 15% discount
      ]
    };
    
    this.totalPriceContainer = null;
    this.quantityBreaksTable = null;
    this.currentVariant = null;
    this.variantChangeUnsubscriber = null;
    this.init();
  }

  init() {
    try {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupSproutPricing());
      } else {
        this.setupSproutPricing();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  isSproutProduct() {
    const currentUrl = window.location.pathname.toLowerCase();
    const productTitle = document.querySelector('.product__title, h1')?.textContent?.toLowerCase() || '';
    const bodyClasses = document.body.className.toLowerCase();
    const productHandle = document.querySelector('[data-product-handle]')?.getAttribute('data-product-handle')?.toLowerCase() || '';
    const metaTitle = document.querySelector('meta[property="og:title"]')?.content?.toLowerCase() || '';
    const pageTitle = document.title.toLowerCase();
    
    return (
      currentUrl.includes('sprout') ||
      productTitle.includes('sprout') ||
      pageTitle.includes('sprout') ||
      metaTitle.includes('sprout') ||
      productHandle.includes('sprout') ||
      bodyClasses.includes('sprout-product-template') ||
      document.querySelector('[data-product-handle*="sprout"]') ||
      document.querySelector('[data-monster-deals]') ||
      window.location.href.includes('sprout') ||
      document.querySelector('template[id*="sprout"]') !== null ||
      // Sprout tier table is rendered for vendor-matched brands too (e.g. Healthy Heights)
      !!document.querySelector('#sprout-quantity-table')
    );
  }

  setupSproutPricing() {
    const isCartContext =
      document.body.classList.contains('template-cart') || window.location.pathname.startsWith('/cart');
    if (isCartContext) {
      return;
    }

    // Only run on sprout products
    if (!this.isSproutProduct()) {
      return;
    }
    
    // Find the quantity table (created by Liquid section)
    this.quantityBreaksTable = document.querySelector('#sprout-quantity-table .quantity-discounts-table');
    if (!this.quantityBreaksTable) {
      return;
    }

    // Skip if table lives inside the cart drawer
    if (this.quantityBreaksTable.closest('cart-drawer')) {
      return;
    }

    // Get the product price
    this.getProductPrice();
    
    // Create total price display
    this.createTotalPriceDisplay();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initial update
    this.updatePricing();
  }

  getProductPrice() {
    // PRIORITY 1: Use current variant data from variant change event
    if (this.currentVariant && this.currentVariant.price) {
      this.quantityBreaks.default = this.currentVariant.price / 100; // Shopify prices are in cents
    } else {
      // PRIORITY 2: Try to get currently selected variant from form inputs
      let selectedVariantInput = document.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]');
      
      // Also check for select elements
      if (!selectedVariantInput) {
        const selectElement = document.querySelector('select[name="id"]');
        if (selectElement && selectElement.selectedOptions.length > 0) {
          selectedVariantInput = selectElement.selectedOptions[0];
        }
      }
      if (selectedVariantInput && selectedVariantInput.value && window.product && window.product.variants) {
        const variant = window.product.variants.find(v => v.id.toString() === selectedVariantInput.value);
        if (variant && variant.price) {
          this.quantityBreaks.default = variant.price / 100; // Shopify prices are in cents
        }
      }
      
      // PRIORITY 3: Try multiple selectors to find the product price from DOM
      if (!this.quantityBreaks.default) {
        const priceSelectors = [
          '.price__sale .price-item--sale',  // Sale price has priority
          '.price__regular .price-item--regular',
          '.price-item--sale',
          '.price-item--regular',
          '.price .price-item',
          '[data-product-price]'
        ];

        for (const selector of priceSelectors) {
          const priceElement = document.querySelector(selector);
          if (priceElement) {
            const priceText = priceElement.textContent || priceElement.innerHTML;
            const match = priceText.replace(/[^\d.,]/g, '').replace(/,/g, '.');
            const price = parseFloat(match);
            if (!isNaN(price) && price > 0) {
              this.quantityBreaks.default = price;
              break;
            }
          }
        }
      }

      // PRIORITY 4: Fallback to form data attributes
      if (!this.quantityBreaks.default) {
        const productForm = document.querySelector('form[data-product-id]');
        if (productForm) {
          const price = productForm.dataset.productPrice;
          if (price) {
            this.quantityBreaks.default = parseFloat(price) / 100; // Convert from cents
          }
        }
      }
    }

    // Calculate tier prices based on discounts
    if (this.quantityBreaks.default) {
      this.quantityBreaks.tiers.forEach(tier => {
        // Match Shopify's "Buy More Save More" discount: the per-can discount is
        // rounded DOWN to the cent, so 10% off $35.99 is $3.59 off = $32.40, not $32.39.
        const baseCents = Math.round(this.quantityBreaks.default * 100);
        tier.price = (baseCents - Math.floor(baseCents * tier.discount + 1e-6)) / 100;
      });
    }

    // Update the Liquid table prices if they need to be updated dynamically
    this.updateTablePrices();
  }

  updateTablePrices() {
    if (!this.quantityBreaksTable || !this.quantityBreaks.default) return;

    const rows = this.quantityBreaksTable.querySelectorAll('tbody tr');
    const basePrice = this.quantityBreaks.default;
    const currentQty = this.getCurrentQuantity();
    
    // Determine current active tier index (0=base, 1=3-5, 2=6-8, 3=9+)
    let activeTierIndex = 0;
    if (currentQty >= 9) activeTierIndex = 3;
    else if (currentQty >= 6) activeTierIndex = 2;
    else if (currentQty >= 3) activeTierIndex = 1;
    
    // Reset and update all unit price cells first
    rows.forEach((row, rowIndex) => {
      const priceCell = row.querySelector('.unit-price-cell');
      if (!priceCell) return;
      // Reset styles
      priceCell.style.textDecoration = 'none';
      priceCell.style.color = '';
      if (rowIndex === 0) {
        priceCell.textContent = `$${basePrice.toFixed(2)}`;
      }
    });

    // Update tier rows with prices
    this.quantityBreaks.tiers.forEach((tier, index) => {
      const row = rows[index + 1];
      
      if (row) {
        const priceCell = row.querySelector('.unit-price-cell');
        if (priceCell) {
          priceCell.textContent = `$${tier.price.toFixed(2)}`;
        }
      }
    });

    // Apply strikethrough to unit prices above the active tier (including base row)
    rows.forEach((row, rowIndex) => {
      if (rowIndex >= activeTierIndex || rowIndex === 0 && activeTierIndex === 0) return;
      const priceCell = row.querySelector('.unit-price-cell');
      if (!priceCell) return;
      priceCell.style.textDecoration = 'line-through';
      priceCell.style.color = '#999';
    });
  }

  createTotalPriceDisplay() {
    // Determine context and target wrapper (inside the quantity input group)
    const contextRoot =
      (this.quantityBreaksTable &&
        this.quantityBreaksTable.closest('.product, .product__info-wrapper, .product__info-container, form')) ||
      document;

    const host = contextRoot.querySelector('[data-total-price-host]');
    const hostIsContainer = host && host.classList.contains('sprout-total-price');
    let totalPriceContainer = hostIsContainer
      ? host
      : contextRoot.querySelector('.sprout-total-price.total-price.current-total');

    if (!totalPriceContainer) {
      totalPriceContainer = document.createElement('div');
      totalPriceContainer.className = 'sprout-total-price total-price current-total';
    }
    totalPriceContainer.style = 'font-size: 1.4rem; font-weight: 600;text-align: left; color: #333;';

    if (host) {
      if (!hostIsContainer) {
        host.innerHTML = '';
        host.appendChild(totalPriceContainer);
      }
      this.totalPriceContainer = totalPriceContainer;
      return;
    }

    const quantityWrapper =
      contextRoot.querySelector('.product-form__input.product-form__quantity') ||
      contextRoot.querySelector('.product-form__quantity');

    if (!quantityWrapper) return;

    // Insert inside the quantity wrapper, immediately after the quantity input element
    const quantityInputEl =
      quantityWrapper.querySelector('quantity-input, input[name="quantity"], .quantity__input') ||
      contextRoot.querySelector('.product-form__quantity quantity-input, input[name="quantity"], .quantity__input');

    if (quantityInputEl && quantityInputEl.parentNode) {
      quantityInputEl.insertAdjacentElement('afterend', totalPriceContainer);
    } else {
      // Fallback: append to the end of the wrapper
      quantityWrapper.appendChild(totalPriceContainer);
    }

    // Store reference to the container
    this.totalPriceContainer = totalPriceContainer;
  }

  setupEventListeners() {
    const debouncedUpdate = this.debounce(() => {
      this.updatePricing();
    }, 300);

    // Listen for quantity changes
    document.addEventListener('input', (e) => {
      if (e.target.matches('input[name="quantity"], .quantity__input, input[type="number"][id*="Quantity"]')) {
        debouncedUpdate();
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="quantity"], .quantity__input, input[type="number"][id*="Quantity"]')) {
        this.updatePricing();
      }
    });

    // Listen for variant changes
    document.addEventListener('variant:change', () => {
      setTimeout(() => {
        this.getProductPrice();
        this.updatePricing();
      }, 50);
    });

    // Listen for Shopify's pub-sub variant change events
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      this.variantChangeUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        this.currentVariant = event.data.variant;
        
        setTimeout(() => {
          this.getProductPrice();
          this.updatePricing();
        }, 100);
      });
    }

    // Listen for quantity button clicks (+ and - buttons)
    document.addEventListener('click', (e) => {
      if (e.target.matches('.quantity__button') || 
          e.target.closest('.quantity__button') ||
          e.target.matches('[name="plus"], [name="minus"]') ||
          e.target.closest('[name="plus"], [name="minus"]')) {
        setTimeout(() => {
          this.updatePricing();
        }, 50);
      }
    });
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  updatePricing() {
    const quantity = this.getCurrentQuantity();
    if (quantity < 1 || !this.quantityBreaks.default) return;

    const { unitPrice, activeRowIndex, tier } = this.calculatePricing(quantity);
    
    // Highlight active row
    this.highlightActiveRow(activeRowIndex);
    
    // Update total price display
    this.updateTotalPriceDisplay(quantity, unitPrice, tier);

    // Keep discount table savings synced with current quantity
    this.updateTablePrices();
  }

  getCurrentQuantity() {
    // Prefer quantity within the same product container as the table
    const table = this.quantityBreaksTable;
    let container = document;
    if (table) container = table.closest('form, .product-form, .product') || document;
    const input = (container.querySelector && container.querySelector('input[name="quantity"], .quantity__input')) || document.querySelector('input[name="quantity"], .quantity__input');
    return input ? parseInt(input.value, 10) || 1 : 1;
  }

  calculatePricing(quantity) {
    let unitPrice = this.quantityBreaks.default;
    let activeRowIndex = 0;
    let tier = null;

    // Check for tier-based discounts
    for (let i = this.quantityBreaks.tiers.length - 1; i >= 0; i--) {
      const currentTier = this.quantityBreaks.tiers[i];
      if (quantity >= currentTier.quantity) {
        unitPrice = currentTier.price;
        activeRowIndex = i + 1; // +1 because base price is row 0
        tier = currentTier;
        break;
      }
    }

    return { unitPrice, activeRowIndex, tier };
  }

  highlightActiveRow(activeRowIndex) {
    if (!this.quantityBreaksTable) return;

    const rows = this.quantityBreaksTable.querySelectorAll('tbody tr');
    
    // Remove existing highlights
    rows.forEach(row => {
      row.classList.remove('orichi-table__row--active', 'orichi-active-row');
    });
    
    // Add highlight to active row
    if (rows[activeRowIndex]) {
      rows[activeRowIndex].classList.add('orichi-table__row--active', 'orichi-active-row');
    }
  }

  updateTotalPriceDisplay(quantity, unitPrice, tier) {
    if (!this.totalPriceContainer) return;

    const totalPrice = quantity * unitPrice;
    const formatPrice = (price) => `$${price.toFixed(2)}`;

    // Calculate original total and savings
    let originalTotalHTML = '';
    
    if (tier && tier.discount > 0) {
      const originalPrice = quantity * this.quantityBreaks.default;
      const savings = originalPrice - totalPrice;
      
      // Only show original price when there's actually a discount
      if (originalPrice > totalPrice && totalPrice > 0) {
        originalTotalHTML = ` <span class="original-total" style="text-decoration: line-through; color: #999; margin-left: 0.5rem;">${formatPrice(originalPrice)}</span>`;
      }
    }

    // Update the total price container with sprout-style layout
    this.totalPriceContainer.innerHTML = `${formatPrice(totalPrice)}${originalTotalHTML}`;
    
    // Dispatch event to notify other scripts (like Recharge listener) of pricing update
    document.dispatchEvent(new CustomEvent('sprout:pricing:updated', {
      detail: {
        quantity: quantity,
        unitPrice: unitPrice,
        totalPrice: totalPrice,
        tier: tier,
        // Real totals, so the Save badge shows what the cart will actually take off
        // rather than back-calculating from the percentage.
        originalTotal: tier && tier.discount > 0 ? quantity * this.quantityBreaks.default : 0,
        savings: tier && tier.discount > 0 ? Math.round((quantity * this.quantityBreaks.default - totalPrice) * 100) / 100 : 0
      }
    }));
  }
}

// Initialize when DOM is ready
if (typeof window !== 'undefined') {
  // Initialize the sprout total pricing
  window.SproutTotalPricing = SproutTotalPricing;
  window.sproutTotalPricingInstance = new SproutTotalPricing();
} 
